import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight, ChevronRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow, KineticLines, GoldButton, EASE } from "@/lib/ui";
import { getServices, SP_LABELS } from "@/data/services";
import { getSite } from "@/data/site";
import { useLang } from "@/i18n/lang";

export default function ServicesIndex() {
  const { lang } = useLang();
  const L = SP_LABELS[lang];
  const { CONTACT } = getSite(lang);
  const services = getServices(lang);
  const jsonLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: L.home, item: "https://glamour-dent.com/" },
      { "@type": "ListItem", position: 2, name: L.services, item: "https://glamour-dent.com/services" },
    ],
  };

  return (
    <>
      <Seo title={lang === "en" ? "Our Services — Luxury Dental Treatments" : "Serviciile Noastre — Tratamente Dentare de Lux"} description={L.servicesLead} path="/services" jsonLd={jsonLd} />

      <section className="relative pt-40 pb-24 bg-ink text-white grain overflow-hidden" data-testid="services-hero">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70vw] h-72 bg-gold/10 blur-[120px] rounded-full" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <nav className="flex items-center gap-2 text-white/40 font-accent text-xs tracking-wide mb-10" data-testid="breadcrumbs">
            <Link to="/" className="hover:text-gold transition-colors">{L.home}</Link><ChevronRight className="w-3 h-3" /><span className="text-white/70">{L.services}</span>
          </nav>
          <Eyebrow dark>{L.services}</Eyebrow>
          <h1 className="font-serif font-light text-5xl md:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
            <KineticLines lines={[L.servicesTitle1, <>{L.servicesTitle2a}<span className="gold-text italic">{L.servicesTitle2gold}</span></>]} />
          </h1>
        </div>
      </section>

      <section className="bg-white text-ink py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Sparkles;
            return (
              <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                <Link to={`/services/${s.slug}`} data-testid={`services-index-card-${s.slug}`} className="group block h-full">
                  <motion.div whileHover={{ y: -8 }} transition={{ duration: 0.4, ease: EASE }} className="h-full rounded-3xl overflow-hidden border border-ink/8 hover:shadow-[0_30px_60px_-25px_rgba(0,0,0,0.25)] transition-shadow duration-500">
                    <div className="relative h-52 overflow-hidden">
                      <img src={s.hero} alt={s.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
                      <div className="absolute top-4 left-4 w-11 h-11 rounded-full glass-dark flex items-center justify-center"><Icon className="w-5 h-5 text-gold" /></div>
                    </div>
                    <div className="p-7">
                      <h3 className="font-serif text-2xl mb-2">{s.name}</h3>
                      <p className="text-ink/55 font-light text-sm leading-relaxed">{s.tagline}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-gold font-accent text-xs tracking-[0.15em] uppercase">{L.discover} <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></span>
                    </div>
                  </motion.div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-charcoal text-white py-28 text-center grain relative">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight">{L.notSurePre}<span className="gold-text italic">{L.notSureGold}</span></h2>
          <p className="mt-6 text-white/55 font-light text-lg">{L.notSureP}</p>
          <div className="mt-10 flex justify-center"><a href={CONTACT.phoneHref}><GoldButton>{L.call} {CONTACT.phone} <Phone className="w-4 h-4" /></GoldButton></a></div>
        </div>
      </section>
    </>
  );
}
